import { useTranslation } from "react-i18next";
import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Pencil, Palette, Grid2X2, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '../ui/button';

interface TutorialSpotlightProps {
  isActive: boolean;
  targetElement?: HTMLElement | null;
  additionalElements?: HTMLElement[];
  title: string;
  description: string;
  onNext?: () => void;
  onSkip?: () => void;
  nextButtonText?: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  showSkip?: boolean;
  customContent?: 'toolbar-icons';
  tooltipDelay?: number;
}

export const TutorialSpotlight: React.FC<TutorialSpotlightProps> = ({
  isActive,
  targetElement,
  additionalElements = [],
  title,
  description,
  onNext,
  onSkip,
  nextButtonText = 'Next',
  customContent,
  position = 'bottom',
  showSkip = true,
  tooltipDelay,
}) => {
  const { t } = useTranslation();
  const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null);
  const [animatedRect, setAnimatedRect] = useState<DOMRect | null>(null);
  const [additionalRects, setAdditionalRects] = useState<DOMRect[]>([]);
  const [animatedAdditionalRects, setAnimatedAdditionalRects] = useState<DOMRect[]>([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const animationFrameRef = useRef<number>();

  // Smooth lerp function for smooth transitions
  const lerp = (start: number, end: number, factor: number) => {
    return start + (end - start) * factor;
  };

  // Update spotlight position with smooth lerp animation
  useEffect(() => {
    if (!isActive || !targetElement) {
      setSpotlightRect(null);
      setAnimatedRect(null);
      setAdditionalRects([]);
      setAnimatedAdditionalRects([]);
      return;
    }

    const updatePosition = () => {
      const rect = targetElement.getBoundingClientRect();
      setSpotlightRect(rect);

      // Initialize animated rect if not set
      if (!animatedRect) {
        setAnimatedRect(rect);
      }
      
      // Update additional element rects
      const addRects = additionalElements.map(el => el.getBoundingClientRect());
      setAdditionalRects(addRects);
      
      // Initialize animated additional rects
      if (animatedAdditionalRects.length === 0 && addRects.length > 0) {
        setAnimatedAdditionalRects(addRects);
      }
    };

    // Initial position
    updatePosition();
    
    // Intelligently scroll to position spotlight and tooltip without overlap
    const scrollIntoView = () => {
      const isMobile = window.innerWidth < 768;
      const rect = targetElement.getBoundingClientRect();
      const screenHeight = window.innerHeight;
      const screenWidth = window.innerWidth;
      
      // Estimate tooltip dimensions
      const tooltipHeight = 250; // Account for content + padding
      const tooltipWidth = isMobile ? screenWidth - 32 : 320;
      const padding = 16;
      const navHeight = isMobile ? 80 : 0;
      
      if (isMobile) {
        // Calculate required space based on position
        if (position === 'top') {
          // Tooltip will be at top, spotlight needs to be below it
          const requiredTopSpace = tooltipHeight + padding + 16; // 16px from top
          const targetTop = requiredTopSpace + rect.height / 2;
          
          if (rect.top < requiredTopSpace) {
            // Scroll down to make room for tooltip at top
            const scrollAmount = requiredTopSpace - rect.top;
            window.scrollBy({
              top: scrollAmount,
              behavior: 'smooth'
            });
          }
        } else {
          // Tooltip at bottom (default) - spotlight needs to be above it
          const requiredBottomSpace = tooltipHeight + padding + navHeight;
          const targetBottom = screenHeight - requiredBottomSpace - rect.height / 2;
          
          if (rect.bottom > targetBottom) {
            // Scroll up to make room for tooltip at bottom
            const scrollAmount = rect.bottom - targetBottom;
            window.scrollBy({
              top: -scrollAmount,
              behavior: 'smooth'
            });
          } else if (rect.top < 16) {
            // Make sure spotlight isn't too close to top
            window.scrollBy({
              top: rect.top - 16,
              behavior: 'smooth'
            });
          }
        }
      } else {
        // Desktop: Check if tooltip would go off-screen and adjust
        let needsScroll = false;
        let scrollAmount = 0;
        
        if (position === 'bottom') {
          const tooltipBottom = rect.bottom + padding + tooltipHeight;
          if (tooltipBottom > screenHeight) {
            needsScroll = true;
            scrollAmount = -(tooltipBottom - screenHeight + 20); // 20px buffer
          }
        } else if (position === 'top') {
          const tooltipTop = rect.top - padding - tooltipHeight;
          if (tooltipTop < 0) {
            needsScroll = true;
            scrollAmount = tooltipTop - 20; // 20px buffer
          }
        } else {
          // For left/right, just center vertically
          const centerY = rect.top + rect.height / 2;
          if (centerY < tooltipHeight / 2 || centerY > screenHeight - tooltipHeight / 2) {
            needsScroll = true;
            scrollAmount = centerY - screenHeight / 2;
          }
        }
        
        if (needsScroll) {
          window.scrollBy({
            top: scrollAmount,
            behavior: 'smooth'
          });
        }
      }
    };
    
    // Longer delay to let page settle and animations complete (especially for modal opening animations)
    const scrollTimer = setTimeout(scrollIntoView, 600);

    // Update on scroll and resize
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      clearTimeout(scrollTimer);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isActive, targetElement, additionalElements, position]);

  // Handle tooltip delay
  useEffect(() => {
    if (!isActive) {
      setShowTooltip(false);
      return;
    }

    // If no delay specified, show immediately
    if (!tooltipDelay) {
      setShowTooltip(true);
      return;
    }

    // Show tooltip after the specified delay
    const timer = setTimeout(() => {
      setShowTooltip(true);
    }, tooltipDelay);

    return () => clearTimeout(timer);
  }, [isActive, tooltipDelay]);

  // Smooth lerp animation
  useEffect(() => {
    if (!spotlightRect || !isActive) return;

    const animate = () => {
      setAnimatedRect((prev) => {
        if (!prev || !spotlightRect) return spotlightRect;

        const lerpFactor = 0.15; // Lower = smoother but slower

        return {
          ...spotlightRect,
          x: lerp(prev.x, spotlightRect.x, lerpFactor),
          y: lerp(prev.y, spotlightRect.y, lerpFactor),
          width: lerp(prev.width, spotlightRect.width, lerpFactor),
          height: lerp(prev.height, spotlightRect.height, lerpFactor),
          top: lerp(prev.top, spotlightRect.top, lerpFactor),
          left: lerp(prev.left, spotlightRect.left, lerpFactor),
          right: lerp(prev.right, spotlightRect.right, lerpFactor),
          bottom: lerp(prev.bottom, spotlightRect.bottom, lerpFactor),
        } as DOMRect;
      });
      
      // Animate additional rects
      setAnimatedAdditionalRects((prev) => {
        if (prev.length !== additionalRects.length) return additionalRects;
        
        return additionalRects.map((rect, i) => {
          const prevRect = prev[i];
          if (!prevRect) return rect;
          
          const lerpFactor = 0.15;
          return {
            ...rect,
            x: lerp(prevRect.x, rect.x, lerpFactor),
            y: lerp(prevRect.y, rect.y, lerpFactor),
            width: lerp(prevRect.width, rect.width, lerpFactor),
            height: lerp(prevRect.height, rect.height, lerpFactor),
            top: lerp(prevRect.top, rect.top, lerpFactor),
            left: lerp(prevRect.left, rect.left, lerpFactor),
            right: lerp(prevRect.right, rect.right, lerpFactor),
            bottom: lerp(prevRect.bottom, rect.bottom, lerpFactor),
          } as DOMRect;
        });
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [spotlightRect, isActive, additionalRects]);

  // Calculate tooltip position - always center on mobile
  const getTooltipPosition = () => {
    if (!animatedRect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

    const padding = 20; // Increased padding to ensure no overlap
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    
    // For mobile, position tooltip based on position prop
    const isMobile = screenWidth < 768;
    
    if (isMobile) {
      // Check if we have additional elements (like navbar buttons) being highlighted
      const hasNavbarHighlight = animatedAdditionalRects.length > 0 && 
        animatedAdditionalRects.some(rect => rect.bottom > screenHeight - 100);
      
      // Position at top if position prop is 'top', otherwise bottom
      if (position === 'top') {
        return {
          position: 'fixed' as const,
          top: '16px',
          left: '16px',
          right: '16px',
          width: 'auto',
          transform: 'none',
          maxHeight: `${animatedRect.top - 32}px`, // Ensure it doesn't overlap spotlight
        };
      }
      
      // Calculate bottom position to ensure no overlap with spotlight
      const spotlightBottom = animatedRect.bottom;
      const availableSpace = screenHeight - spotlightBottom - (hasNavbarHighlight ? 80 : 0);
      
      return {
        position: 'fixed' as const,
        bottom: hasNavbarHighlight ? '96px' : '88px', // Above the navbar
        left: '16px',
        right: '16px',
        width: 'auto',
        transform: 'none',
        maxHeight: `${availableSpace - 100}px`, // Ensure it fits above navbar
      };
    }

    // Desktop positioning with smart overlap prevention
    const tooltipHeight = 250; // Increased estimate for safety
    const tooltipWidth = 320;
    let top = 0;
    let left = 0;
    let style: any = {};

    switch (position) {
      case 'bottom':
        top = animatedRect.bottom + padding;
        left = animatedRect.left + animatedRect.width / 2;
        
        // Check if tooltip would go off-screen at bottom
        if (top + tooltipHeight > screenHeight) {
          // Flip to top instead
          top = animatedRect.top - tooltipHeight - padding;
        }
        
        style = {
          top: `${Math.max(padding, top)}px`,
          left: `${left}px`,
          transform: 'translateX(-50%)',
        };
        break;
        
      case 'top':
        top = animatedRect.top - tooltipHeight - padding;
        left = animatedRect.left + animatedRect.width / 2;
        
        // Check if tooltip would go off-screen at top
        if (top < 0) {
          // Flip to bottom instead
          top = animatedRect.bottom + padding;
        }
        
        style = {
          top: `${Math.max(padding, top)}px`,
          left: `${left}px`,
          transform: 'translateX(-50%)',
        };
        break;
        
      case 'left':
        top = animatedRect.top + animatedRect.height / 2;
        left = animatedRect.left - tooltipWidth - padding;
        
        // Check if tooltip would go off-screen on left
        if (left < 0) {
          // Switch to right instead
          left = animatedRect.right + padding;
        }
        
        style = {
          top: `${top}px`,
          left: `${Math.max(padding, left)}px`,
          transform: 'translateY(-50%)',
        };
        break;
        
      case 'right':
        top = animatedRect.top + animatedRect.height / 2;
        left = animatedRect.right + padding;
        
        // Check if tooltip would go off-screen on right
        if (left + tooltipWidth > screenWidth) {
          // Switch to left instead
          left = animatedRect.left - tooltipWidth - padding;
        }
        
        style = {
          top: `${top}px`,
          left: `${Math.max(padding, left)}px`,
          transform: 'translateY(-50%)',
        };
      case 'center':
      default:
        return {
          position: 'fixed' as const,
          top: '33.33%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        };
    }
  };

  if (!isActive) return null;

  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[9999]"
          style={{ pointerEvents: 'none' }}
        >
          {/* Dark overlay with spotlight cutout - only show when target element exists */}
          {targetElement && (
            <svg
              className="absolute inset-0 w-full h-full"
              style={{ pointerEvents: 'none' }}
            >
              <defs>
                <mask id="spotlight-mask">
                  <rect x="0" y="0" width="100%" height="100%" fill="white" />
                  {animatedRect && (
                    <rect
                      x={animatedRect.x - 8}
                      y={animatedRect.y - 8}
                      width={animatedRect.width + 16}
                      height={animatedRect.height + 16}
                      rx="12"
                      fill="black"
                    />
                  )}
                  {animatedAdditionalRects.map((rect, i) => (
                    <rect
                      key={i}
                      x={rect.x - 8}
                      y={rect.y - 8}
                      width={rect.width + 16}
                      height={rect.height + 16}
                      rx="12"
                      fill="black"
                    />
                  ))}
                </mask>
              </defs>
              <rect
                x="0"
                y="0"
                width="100%"
                height="100%"
                fill="rgba(0, 0, 0, 0.75)"
                mask="url(#spotlight-mask)"
              />
            </svg>
          )}

          {/* Glowing border around target */}
          {animatedRect && (
            <motion.div
              className="absolute border-4 border-blue-500 rounded-xl shadow-lg shadow-blue-500/50"
              style={{
                left: `${animatedRect.x - 8}px`,
                top: `${animatedRect.y - 8}px`,
                width: `${animatedRect.width + 16}px`,
                height: `${animatedRect.height + 16}px`,
                pointerEvents: 'none',
              }}
              animate={{
                boxShadow: [
                  '0 0 20px rgba(59, 130, 246, 0.5)',
                  '0 0 40px rgba(59, 130, 246, 0.8)',
                  '0 0 20px rgba(59, 130, 246, 0.5)',
                ],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          )}
          
          {/* Glowing borders around additional elements - only show if we have a target element */}
          {targetElement && animatedAdditionalRects.map((rect, i) => (
            <motion.div
              key={i}
              className="absolute border-4 border-blue-500 rounded-xl shadow-lg shadow-blue-500/50"
              style={{
                left: `${rect.x - 8}px`,
                top: `${rect.y - 8}px`,
                width: `${rect.width + 16}px`,
                height: `${rect.height + 16}px`,
                pointerEvents: 'none',
              }}
              animate={{
                boxShadow: [
                  '0 0 20px rgba(59, 130, 246, 0.5)',
                  '0 0 40px rgba(59, 130, 246, 0.8)',
                  '0 0 20px rgba(59, 130, 246, 0.5)',
                ],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          ))}

          {/* Tooltip - only show after delay */}
          {showTooltip && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
              className="bg-background/60 backdrop-blur-md border border-border rounded-lg shadow-2xl p-6"
              style={{
                ...getTooltipPosition(),
                pointerEvents: 'auto',
                zIndex: 10000,
              }}
            >
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-lg font-semibold text-foreground">{title}</h3>
              {showSkip && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 -mt-1 -mr-2"
                  onClick={onSkip}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <p className="text-sm text-muted-foreground mb-4">{description}</p>
            
            {customContent === 'toolbar-icons' && (
              <div className="space-y-2 mb-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Pencil className="w-4 h-4" />
                  <span>{t('ui.text.edit_daily_goal')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  <span>{t('ui.text.change_color')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Grid2X2 className="w-4 h-4" />
                  <span>{t('ui.text.switch_to_line_graph')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <ArrowUp className="w-3.5 h-3.5" />
                  <ArrowDown className="w-3.5 h-3.5" />
                  <span>{t('ui.text.adjust_order')}</span>
                </div>
              </div>
            )}
            
            {onNext && (
              <div className="flex justify-end gap-2">
                {showSkip && (
                  <Button variant="outline" size="sm" onClick={() => {
                    console.log('[TutorialSpotlight] Skip button clicked');
                    onSkip?.();
                  }}>{t('ui.text.skip_tutorial')}</Button>
                )}
                <Button size="sm" onClick={() => {
                  console.log('[TutorialSpotlight] Next/Finish button clicked:', nextButtonText);
                  onNext();
                }} className="min-w-[80px]">
                  {nextButtonText}
                </Button>
              </div>
            )}
          </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
